import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useConsent, saveConsent } from '@/lib/consent';

const AREA_TYPES = ['freiflaeche', 'acker', 'gewerbe', 'dach', 'konversion', 'sonstige'];
const OWNERSHIPS = ['eigentuemer', 'pachter', 'makler', 'sonstige'];
const MAX_FILES = 5;
const MAX_FILE_MB = 10;
const FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

const inputCls = 'w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-white placeholder-slate-500 focus:border-green-500 focus:outline-none';
const labelCls = 'mb-1 block text-sm text-slate-300';

const LandOffer = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', location: '', parcel: '', area_size: '', area_unit: 'ha',
    area_type: 'freiflaeche', ownership: 'eigentuemer', grid_distance_km: '', availability: '', message: '',
    website: '' // Honeypot
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [recaptchaConfig, setRecaptchaConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState(0);
  const [done, setDone] = useState(false);
  const recaptchaRef = useRef(null);
  const { consent } = useConsent();
  const recaptchaAllowed = !!(consent && consent.recaptcha);

  useEffect(() => {
    axios.get(`${API}/settings/recaptcha/public`)
      .then((r) => setRecaptchaConfig(r.data))
      .catch(() => setRecaptchaConfig({ site_key: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '', enabled: true }));
  }, []);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const next = [...files];
    for (const f of picked) {
      if (next.length >= MAX_FILES) { toast.error(t('landOffer.filesTooMany', { n: MAX_FILES })); break; }
      if (!FILE_TYPES.includes(f.type)) { toast.error(t('landOffer.fileBadType', { name: f.name })); continue; }
      if (f.size > MAX_FILE_MB * 1024 * 1024) { toast.error(t('landOffer.fileTooBig', { name: f.name, mb: MAX_FILE_MB })); continue; }
      next.push(f);
    }
    setFiles(next);
  };
  const recaptchaActive = !!(recaptchaConfig && recaptchaConfig.enabled && recaptchaConfig.site_key);

  const submit = async (e) => {
    e.preventDefault();
    if (!consentChecked) { toast.error(t('landOffer.consentRequired')); return; }
    let token = 'disabled';
    if (recaptchaActive) {
      token = recaptchaRef.current?.getValue();
      if (!token) { toast.error(t('landOffer.recaptchaRequired')); return; }
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/land-offers`, {
        ...form,
        phone: form.phone || null,
        parcel: form.parcel || null,
        area_size: parseFloat(String(form.area_size).replace(',', '.')),
        grid_distance_km: form.grid_distance_km === '' ? null : parseFloat(String(form.grid_distance_km).replace(',', '.')),
        availability: form.availability || null,
        message: form.message || null,
        consent: true,
        recaptcha_token: token
      });
      let failed = 0;
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append('token', res.data.upload_token);
          fd.append('file', f);
          await axios.post(`${API}/land-offers/${res.data.id}/files`, fd);
        } catch (upErr) { failed += 1; }
      }
      setFailedFiles(failed);
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('landOffer.error'));
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fadeIn bg-slate-900 min-h-screen text-white relative" data-testid="land-offer-page">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6"><LanguageSwitcher /></div>
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-8 text-sm">
            <ArrowLeft size={16} className="mr-2" />{t('landOffer.back')}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('landOffer.title')}</h1>
          <p className="text-slate-300 mb-8">{t('landOffer.intro')}</p>

          {done ? (
            <div className="rounded-2xl border border-green-500/40 bg-green-500/10 p-8 text-center" data-testid="land-offer-success">
              <CheckCircle className="mx-auto mb-4 text-green-400" size={48} />
              <h2 className="text-2xl font-bold mb-2">{t('landOffer.successTitle')}</h2>
              <p className="text-slate-300">{t('landOffer.successText')}</p>
              {failedFiles > 0 && <p className="mt-3 text-amber-300">{t('landOffer.filesFailed', { n: failedFiles })}</p>}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="land-offer-form">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><label className={labelCls} htmlFor="name">{t('landOffer.name')} *</label>
                  <input id="name" name="name" required minLength={2} className={inputCls} value={form.name} onChange={set} /></div>
                <div><label className={labelCls} htmlFor="email">{t('landOffer.email')} *</label>
                  <input id="email" name="email" type="email" required className={inputCls} value={form.email} onChange={set} /></div>
                <div><label className={labelCls} htmlFor="phone">{t('landOffer.phone')}</label>
                  <input id="phone" name="phone" type="tel" className={inputCls} value={form.phone} onChange={set} /></div>
                <div><label className={labelCls} htmlFor="ownership">{t('landOffer.ownership')}</label>
                  <select id="ownership" name="ownership" className={inputCls} value={form.ownership} onChange={set}>
                    {OWNERSHIPS.map((o) => <option key={o} value={o}>{t(`landOffer.own_${o}`)}</option>)}
                  </select></div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><label className={labelCls} htmlFor="location">{t('landOffer.location')} *</label>
                  <input id="location" name="location" required minLength={2} className={inputCls} value={form.location} onChange={set} /></div>
                <div><label className={labelCls} htmlFor="parcel">{t('landOffer.parcel')}</label>
                  <input id="parcel" name="parcel" className={inputCls} value={form.parcel} onChange={set} /></div>
                <div className="flex gap-2">
                  <div className="flex-1"><label className={labelCls} htmlFor="area_size">{t('landOffer.size')} *</label>
                    <input id="area_size" name="area_size" required inputMode="decimal" className={inputCls} value={form.area_size} onChange={set} /></div>
                  <div className="w-24"><label className={labelCls} htmlFor="area_unit">{t('landOffer.unit')}</label>
                    <select id="area_unit" name="area_unit" className={inputCls} value={form.area_unit} onChange={set}>
                      <option value="ha">ha</option><option value="m2">m²</option>
                    </select></div>
                </div>
                <div><label className={labelCls} htmlFor="area_type">{t('landOffer.type')}</label>
                  <select id="area_type" name="area_type" className={inputCls} value={form.area_type} onChange={set}>
                    {AREA_TYPES.map((a) => <option key={a} value={a}>{t(`landOffer.type_${a}`)}</option>)}
                  </select></div>
                <div><label className={labelCls} htmlFor="grid_distance_km">{t('landOffer.grid')}</label>
                  <input id="grid_distance_km" name="grid_distance_km" inputMode="decimal" className={inputCls} value={form.grid_distance_km} onChange={set} /></div>
                <div><label className={labelCls} htmlFor="availability">{t('landOffer.availability')}</label>
                  <input id="availability" name="availability" className={inputCls} value={form.availability} onChange={set} /></div>
              </div>

              <div><label className={labelCls} htmlFor="message">{t('landOffer.message')}</label>
                <textarea id="message" name="message" rows={4} maxLength={3000} className={inputCls} value={form.message} onChange={set} /></div>

              <div>
                <label className={labelCls} htmlFor="files">{t('landOffer.files')}</label>
                <input id="files" type="file" multiple accept="image/jpeg,image/png,application/pdf" onChange={pickFiles}
                  className="block w-full text-sm text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-slate-700 file:px-3 file:py-2 file:text-white" data-testid="land-files" />
                <p className="mt-1 text-xs text-slate-400">{t('landOffer.filesHint', { n: MAX_FILES, mb: MAX_FILE_MB })}</p>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-slate-200">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center gap-2"><Paperclip size={14} />{f.name}
                        <button type="button" className="ml-auto text-xs underline text-slate-400" onClick={() => setFiles(files.filter((_, k) => k !== i))}>{t('landOffer.fileRemove')}</button></li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Honeypot: für Menschen unsichtbar */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
                <label htmlFor="website">Website</label>
                <input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set} />
              </div>

              <label className="flex items-start gap-3 text-sm text-slate-300">
                <input type="checkbox" className="mt-1" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} data-testid="land-consent" />
                <span>{t('landOffer.consent')}{' '}<Link to="/bess/datenschutz" className="underline">{t('landOffer.privacy')}</Link></span>
              </label>

              {recaptchaConfig === null ? null : recaptchaActive && !recaptchaAllowed ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="recaptcha-consent">
                  <p className="mb-3">{t('consent.recaptchaNeeded')}</p>
                  <button type="button" onClick={() => saveConsent({ recaptcha: true })}
                    className="rounded-md border border-amber-400 bg-white px-4 py-2 font-medium text-amber-900 hover:bg-amber-100">
                    {t('consent.recaptchaLoad')}
                  </button>
                </div>
              ) : recaptchaActive ? (
                <div className="flex justify-center"><ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaConfig.site_key} /></div>
              ) : null}

              <button type="submit" disabled={loading || (recaptchaActive && !recaptchaAllowed)}
                className="w-full rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-green-500 px-8 py-4 text-lg font-bold text-white shadow-xl disabled:opacity-50"
                data-testid="land-submit">
                {loading ? t('landOffer.sending') : t('landOffer.submit')}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default LandOffer;
